import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  message,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Upload,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import { Trash, Plus, Inbox } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatNumber,
  formatNumbers,
  formatString,
  formatStrings,
  handleChangePage,
  handlePageSizeChange,
  navigateTo,
} from "../../Utils/Common";
import CustomTable from "../../Components/Table/CustomTable";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { stat } from "fs";
import { useParams } from "react-router";

const { Dragger } = Upload;
const { Option } = Select;

interface PurchaseOrderFormProps {
  add?: boolean;
  edit?: boolean;
  view?: boolean;
  approval?: boolean;
}

const PurchaseOrderForm: React.FC<PurchaseOrderFormProps> = ({ add, edit, view, approval }) => {
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState("");
  const [filterConfig, setFilterConfig]: any = useState({ page: 1 });
  const [dataSize, setDataSize] = useState(0);
  const [form] = Form.useForm();
  const [selectedRow, setSelectedRow]: any = useState({});
  const [dataSource, setDataSource] = useState([]);
  const [expenseTypes, setExpenseTypes]: any = useState([]);
  const [branches, setBranches]: any = useState([]);
  const [purchaseRequests, setPurchaseRequests]: any = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [purchaseRequestId, setPurchaseRequestId] = useState([]);
  const itemsArray = useWatch("itemsArray", form);
  const [prSelected, setPrSelected] = useState(false);
  const [withholdingTaxPercent, setWithholdingTaxPercent] = useState(0);
  const { id: purchase_request_id } = useParams<{ id: string }>();
  const isReadOnly = view || add || approval;

  function clearFields() {
    form.resetFields();
  }

  // Subtotal remains the same
  const subTotal = (itemsArray || []).reduce((sum: number, item: any) => {
    const qty = Number(item?.qty) || 0;
    const price = Number(item?.price) || 0;
    return sum + qty * price;
  }, 0);

  // Calculate withholding tax
  const withholdingTaxAmount = subTotal * (withholdingTaxPercent / 100);

  // Grand total = Subtotal - Withholding Tax
  const grandTotal = subTotal - withholdingTaxAmount;

  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(`${file.name} exceeds the 2MB size limit.`);
    }
    return isLt2M ? false : Upload.LIST_IGNORE;
  };

  async function submitForm(data: any) {
    const formattedItemsArray = (data.itemsArray || []).map((item: any) => ({
      ...item,
      total_amount: parseFloat(
        ((Number(item.qty) || 0) * (Number(item.amount) || 0)).toFixed(2)
      ),
    }));

    const formattedData = {
      ...data,
      expense_date: data.transaction_date
        ? dayjs(data.transaction_date).format("YYYY-MM-DD")
        : "",
      itemsArray: formattedItemsArray,
      grand_total: grandTotal,
      wht_percent: withholdingTaxPercent,
      wht_amount: withholdingTaxAmount,
      purchase_order_id: purchase_request_id
    };

    const formData = new FormData();

    for (const key in formattedData) {
      if (key === "itemsArray") {
        formData.append(key, JSON.stringify(formattedData[key]));
      } else if (key === "attachment") {
        // Ant Design file objects — unwrap originFileObj
        const attachments = formattedData[key] || [];
        attachments.forEach((file: any) => {
          if (file.originFileObj) {
            formData.append("attachments[]", file.originFileObj);
          }
        });
      } else {
        const value = formattedData[key];
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      }
    }

    let response;
    setLoading("submit");
    if (add) {
      response = await postRequest(
        `purchase_requests/change_status`,
        { action: "approved", purchase_request_id: purchaseRequestId },
        true
      );
    } else if (edit) {
      response = await postRequest( // 👈 no const
        `purchase_orders/update`,
        formData,
        true
      );
    } else {
      setLoading("");
      navigateTo("/purchase-order");
      return;
    }

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
      }, 1000);
      navigateTo("/purchase-order");
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} purchase order.`
      );
    }
  }

  const handleApprove = () => {
    Modal.confirm({
      title: "Are you sure you want to approve this purchase request?",
      onOk: async () => {
        setLoading("submit");
        const response = await postRequest(
          `purchase_orders/change_status`,
          { purchase_order_id : purchase_request_id, action: "approved" },
          true
        );
        if (response.success) {
          message.success(formatString(response.data.message));
          setTimeout(() => {
            clearFields();
            setShowModal("");
            setLoading("");
            navigateTo("/purchase-order");
          }, 1000);
        } else {
          setLoading("");
          message.error(
            response?.data?.response?.data?.messages?.error_messages?.[0] ??
              `Failed to approve purchase request.`
          );
        }
      },
    });
  };

  const reasonRef = useRef<string>("");

  const handleDisapprove = () => {
    reasonRef.current = "";
    Modal.confirm({
      title: "Are you sure you want to disapprove this purchase request?",
      content: (
        <Input.TextArea
          placeholder="Enter reason for disapproval"
          onChange={(e) => {
            reasonRef.current = e.target.value; // 👈 use ref instead of state
          }}
        />
      ),
      onOk: async () => {
        if (!reasonRef.current) {
          message.error("Please provide a reason for disapproval.");
          return Promise.reject(); // 👈 keeps modal open
        }

        setLoading("submit");
        const response = await postRequest(
          `purchase_orders/change_status`,
          { purchase_order_id : purchase_request_id, action: "disapproved", reason: reasonRef.current }, // 👈
          true
        );
        if (response.success) {
          message.success(formatString(response.data.message));
          setTimeout(() => {
            clearFields();
            setShowModal("");
            setLoading("");
            navigateTo("/purchase-order");
          }, 1000);
        } else {
          setLoading("");
          message.error(
            response?.data?.response?.data?.messages?.error_messages?.[0] ??
              `Failed to disapprove purchase request.`
          );
        }
      },
    });
  };

  async function fetchBranches() {
    setBranches([]);
    setLoading("fetch");
    var response = await getRequest(`branches/get`, true);
    if (response.success) {
      setBranches(response.data.data);
    }
    setLoading("");
  }

  async function fetchExpenseTypes() {
    setExpenseTypes([]);
    setLoading("fetch");
    var response = await getRequest(`expense_types/get`, true);
    if (response.success) {
      setExpenseTypes(response.data.data);
    }
    setLoading("");
  }

  async function fetchPurchaseRequests() {
    setPurchaseRequests([]);
    setLoading("fetch");
    var response = await getRequest(`purchase_requests/get`, { status: "pending" }, true);
    if (response.success) {
      setPurchaseRequests(response.data.data);
    }
    setLoading("");
  }

  async function fetchSuppliers() {
    setSuppliers([]);
    setLoading("fetch");
    var response = await getRequest(`suppliers/get`, true);
    if (response.success) {
      setSuppliers(response.data.data);
    }
    setLoading("");
  }

  async function fetchItems() {
    setItems([]);
    setLoading("fetch");
    var response = await getRequest(`items/get`, true);
    if (response.success) {
      setItems(response.data.data);
    }
    setLoading("");
  }

  const fetchPurchaseRequestData = (value?: any) => {
    const id = value ?? purchase_request_id; // 👈 use value if provided, else URL param

    if (!id) {
      form.resetFields();
      setPrSelected(false);
      setWithholdingTaxPercent(1);
      return;
    }

    setLoading("fetch");

    if (add) {
      getRequest(`purchase_requests/get`, { purchase_request_id: id }, true).then((response) => {
        if (response.success) {
          const data = response.data.data[0];

          const items = data.items.map((row: any) => ({
            item_id:         row.item_id,
            item_name:       row.item_name,
            expense_type_id: Number(row.expense_type_id),
            qty:             Number(row.qty),
            unit:            row.unit,
            price:           Number(row.price),
          }));

          form.setFieldsValue({
            supplier_id:      Number(data.supplier_id),
            supplier_name:    data.supplier_name,
            branch_id:        Number(data.branch_id),
            transaction_date: data.expense_date ? dayjs(data.expense_date) : null,
            remarks:          data.remarks,
            itemsArray:       items,
          });

          setWithholdingTaxPercent(Number(data.wht_percentage) || 1);
          setPrSelected(true);
        }
        setLoading("");
      });
    } else {
      // edit/view — use purchase_order_id from URL
      getRequest(`purchase_orders/get`, { purchase_order_id: id }, true).then((response) => {
        if (response.success) {
          const data = response.data.data[0];

          const items = data.items.map((row: any) => ({
            item_id:         row.item_id,
            item_name:       row.item_name,
            expense_type_id: Number(row.expense_type_id),
            qty:             Number(row.qty),
            unit:            row.unit,
            price:           Number(row.price),
          }));

          form.setFieldsValue({
            purchase_request_id: Number(data.purchase_request_id),
            supplier_id:         Number(data.supplier_id),
            supplier_name:       data.supplier_name,
            branch_id:           Number(data.branch_id),
            transaction_date:    data.expense_date ? dayjs(data.expense_date) : null,
            remarks:             data.remarks,
            itemsArray:          items,
          });

          setWithholdingTaxPercent(Number(data.wht_percentage) || 1);
          setPrSelected(true);
        }
        setLoading("");
      });
    }
  };

  const handlePRChange = (value: any) => {
    fetchPurchaseRequestData(value); // 👈 passes selected value
  };

  useEffect(() => {
    if (add) {
      console.log("in Add Mode");
    } else if (edit || view || approval) {
      fetchPurchaseRequestData();
    }
  }, []);

  useEffect(() => {
    fetchExpenseTypes();
    fetchBranches();
    fetchPurchaseRequests();
    fetchSuppliers();
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title={add ? "Add Purchase Order" : edit ? "Edit Purchase Order" : "View Purchase Order"}
              withDateRangeFilter={false}
              withAddButton={false}
              withSearchBtn={false}
            />
          </Col>
          <div className="form-container">
            <Col span={24}>
              <Form
                form={form}
                onFinish={(data) => submitForm(data)}
                layout="vertical"
              >
                <Row className="w-full" gutter={[10, 10]}>
                  {add && (
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="PR No."
                        name="purchase_request_id"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <Select
                          placeholder="Select purchase request"
                          showSearch
                          allowClear
                          optionFilterProp="label"
                          onChange={handlePRChange}
                          options={purchaseRequests.map((info: any) => ({
                            label: `PR No. - ${info.id}`,
                            value: Number(info.id),
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Col xs={24} lg={12}>
                    <Form.Item
                      label="Supplier Name"
                      name="supplier_name"
                      rules={[{ required: true, whitespace: true, message: "This field is required." }]}
                    >
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      label="Branch"
                      name="branch_id"
                      rules={[{ required: true, message: "This field is required." }]}
                    >
                      <Select
                        placeholder="Select branch"
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        disabled={isReadOnly}
                        options={branches.map((branch: any) => ({
                          label: branch.name,
                          value: Number(branch.id),
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      label="Transaction Date"
                      name="transaction_date"
                      rules={[{ required: true, message: "This field is required." }]}
                    >
                      <DatePicker className="w-full" format="MMMM D, YYYY" disabled={isReadOnly} />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item label="Remarks" name="remarks">
                      <Input.TextArea disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <span className="edit-label mb-2">
                      <label className="text-red-500 me-1"> *</label>List of Items:
                    </span>
                    <Form.List name="itemsArray" initialValue={[{}]}>
                      {(fields, { add, remove }) => (
                        <>
                          <Col xs={24}>
                            <div className="overflow-auto max-h-[600px]">
                              <table className="w-full table-fixed border border-collapse min-w-[800px]">
                                <thead>
                                <tr className="bg-primary text-white font-bold border">
                                  <th className="p-2 border w-[200px]">Item Name</th>
                                  <th className="p-2 border w-[150px]">Expense Type</th>
                                  <th className="p-2 border w-[80px]">Qty</th>
                                  <th className="p-2 border w-[100px]">Price</th>
                                  <th className="p-2 border w-[100px]">Total</th>
                                  {!isReadOnly && (
                                    <th className="p-2 border w-[60px] text-center">Action</th>
                                  )}
                                </tr>
                              </thead>
                                <tbody>
                                  {fields.map((field) => (
                                    <tr key={field.key} className="border bg-white">
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "item_name"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Input disabled={isReadOnly} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "expense_type_id"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Select
                                            className="w-full"
                                            placeholder="Select expense type"
                                            showSearch
                                            allowClear
                                            optionFilterProp="label"
                                            disabled={isReadOnly} // 👈
                                            options={expenseTypes.map((type: any) => ({
                                              label: type.name,
                                              value: Number(type.id),
                                            }))}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "qty"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Input
                                            type="number"
                                            min={1}
                                            disabled={isReadOnly} // 👈
                                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "price"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Input
                                            type="number"
                                            min={0}
                                            step="0.01"
                                            disabled={isReadOnly} // 👈
                                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item shouldUpdate noStyle>
                                          {() => {
                                            const qty = form.getFieldValue(["itemsArray", field.name, "qty"]);
                                            const price = form.getFieldValue(["itemsArray", field.name, "price"]);
                                            const total = (Number(qty) || 0) * (Number(price) || 0);
                                            return <Input value={total.toFixed(2)} disabled />;
                                          }}
                                        </Form.Item>
                                      </td>
                                      {!isReadOnly && ( // 👈
                                        <td className="p-2 border text-center">
                                          {fields.length !== 1 && (
                                            <Button
                                              onClick={() => remove(field.name)}
                                              icon={<Trash size={17} />}
                                              danger
                                              type="text"
                                            />
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Col>
                          <Col xs={24}>
                            <Row>
                              <Col span={17} className="mt-2">
                                {!prSelected && (
                                  <Button type="primary" onClick={() => add()} icon={<Plus size={16} />}>
                                    Add Item
                                  </Button>
                                )}
                              </Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Subtotal:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {subTotal.toFixed(2)}</Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-5">
                                <Radio.Group
                                  value={withholdingTaxPercent}
                                  onChange={(e) => setWithholdingTaxPercent(e.target.value)}
                                  disabled={isReadOnly} // 👈
                                >
                                  <Radio value={0}>0%</Radio>
                                  <Radio value={1}>1%</Radio>
                                  <Radio value={2}>2%</Radio>
                                </Radio.Group>
                              </Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Calculated Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {withholdingTaxAmount.toFixed(2)}</Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Grand Total:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(grandTotal)}</Col>
                            </Row>
                          </Col>
                        </>
                      )}
                    </Form.List>
                  </Col>

                  <Col xs={24} className="mt-5 pt-5">
                    <Form.Item
                      name="attachment"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                    >
                      <label className="text-red-500 me-1"> *</label>Attachment:
                      <Dragger name="file" beforeUpload={handleBeforeUpload} accept=".pdf,image/*">
                        <p className="ant-upload-drag-icon flex justify-center items-center text-3xl">
                          <Inbox />
                        </p>
                        <p className="ant-upload-text">Click or drag file to upload</p>
                        <p className="ant-upload-hint">Supports PDF, Image formats.</p>
                      </Dragger>
                      <p className="text-textColor pt-2">Note: Maximum file size is 2MB per file.</p>
                    </Form.Item>
                  </Col>

                  <Col lg={24} className="mt-5 flex justify-end gap-3">
                    <Form.Item>
                      <Button onClick={() => { navigateTo("/purchase-order"); clearFields(); }}>
                        Cancel
                      </Button>
                    </Form.Item>

                    {!view && !approval && (
                      <Form.Item>
                        <Button
                          htmlType="submit"  // 👈 only for add/edit
                          type="primary"
                          loading={loading === "submit"}
                        >
                          {add ? "Submit" : "Update"}
                        </Button>
                      </Form.Item>
                    )}

                    {approval && (
                      <>
                        <Form.Item>
                          <Button
                            danger
                            type="primary"
                            onClick={handleDisapprove} // 👈 no htmlType="submit"
                            loading={loading === "submit"}
                          >
                            Disapprove
                          </Button>
                        </Form.Item>
                        <Form.Item>
                          <Button
                            type="primary"
                            onClick={handleApprove} // 👈 no htmlType="submit"
                            loading={loading === "submit"}
                          >
                            Approve
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Col>
                </Row>
              </Form>
            </Col>
          </div>
        </Row>
      </Layout>
      <PersistentFooter />
    </Layout>
  );
};

export default PurchaseOrderForm;
